import { applySampleItemPrivacy } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';
import { applyAiReportScopeToEvidence } from '@process-advisor/process-miner/consultant/aiReportScope';

export const HEALTH_DEBUG_BUILD_ID = 'health-align-debug-v6-ai-scope-2026-04-25';

const toFixedOrNull = (value, digits = 2) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits) : null;
};

const stableStringify = (value) => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
};

export const buildConsultantRuntimeArgs = ({
  issueTypes = [],
  metrics = [],
  metricsTotal = null,
  processSteps = [],
  variants = [],
  rawSampleItems = [],
  privacyRules = {},
  fieldUsage = {},
  statusTypeSummary = null,
  statusTypeMappings = [],
  activityType = null,
  activityContext = null,
  extra = {},
  reportScope = null,
} = {}) => {
  const issueTypeStr = issueTypes.length === 1 ? `Work Type = ${issueTypes[0]}` : `Work Types = ${issueTypes.join(' or ')}`;

  // Report scope is applied first. Attribute privacy/anonymisation is applied after
  // so the AI sees only the scoped evidence, with privacy rules still enforced.
  const scopedEvidence = applyAiReportScopeToEvidence({
    rawSampleItems,
    processSteps,
    variants,
    scope: reportScope || {},
  });
  const privacy = applySampleItemPrivacy(scopedEvidence.rawSampleItems, privacyRules);
  const scopedProcessSteps = scopedEvidence.processSteps || processSteps;
  const scopedVariants = scopedEvidence.variants || variants;

  const metricsHistory = (metrics || []).filter((m) => Number(m?.work_items) > 0).map((m) => ({
    work_items: m.work_items,
    variants: m.variants,
    activities: m.activities,
    steps: m.steps,
    avg_transitions: m.avg_transitions,
    avg_duration_hours: toFixedOrNull((Number(m.cycle_time) || 0) / 3600),
    std_deviation_hours: toFixedOrNull((Number(m.std_deviation) || 0) / 3600),
    rework_pct: toFixedOrNull((Number(m.rework_rate) || 0) * 100),
    avg_flow_efficiency: m.avg_flow_efficiency ?? null,
  }));

  const transitionsJSON = (scopedProcessSteps || []).slice(0, 40).map((t) => ({
    transition: t.step,
    from: t.from_activity,
    to: t.to_activity,
    items: t.tasks,
    avg_hours: toFixedOrNull(t.avg_duration_hrs),
    rework: t.loop,
    std_hours: toFixedOrNull(t.std_deviation_hrs),
    pct_total_items: t.perc_total_tasks != null ? toFixedOrNull(Number(t.perc_total_tasks) * 100) : null,
  }));

  const variantsJSON = (scopedVariants || []).slice(0, 30).map((v) => ({
    variant: v.variant,
    id: v.variant_id,
    items: v.tasks,
    pct_total: v.perc_total_tasks,
    avg_hours: v.avg_duration_hrs,
    rework: v.loop,
    loops: v.loops,
    std_hours: v.std_deviation_hrs,
  }));

  const runtimeArgs = {
    issueTypeStr,
    metricsHistory,
    metricsTotal,
    transitionsJSON,
    variantsJSON,
    sampleItems: privacy.rows,
    fieldUsage: fieldUsage || {},
    statusTypeSummary: statusTypeSummary || null,
    statusTypeMappings: statusTypeMappings || [],
    activityType: activityType || null,
    activityContext: activityContext || null,
    reportScopeSummary: scopedEvidence.summary || null,
    reportInstructions: scopedEvidence.summary?.instructions || '',
    ...extra,
  };

  const debug = {
    buildId: HEALTH_DEBUG_BUILD_ID,
    signature: stableStringify({
      metricsHistory,
      transitionsJSON,
      variantsJSON,
      sampleItemsCount: Array.isArray(privacy.rows) ? privacy.rows.length : 0,
      fieldUsageKeys: fieldUsage ? Object.keys(fieldUsage).sort() : [],
      statusTypeMappingsCount: Array.isArray(statusTypeMappings) ? statusTypeMappings.length : 0,
      statusTypeSummary,
      activityType,
      activityContext,
      extra,
      reportScopeSummary: scopedEvidence.summary || null,
    }),
    summary: {
      metricsRows: metricsHistory.length,
      transitionsRows: transitionsJSON.length,
      variantsRows: variantsJSON.length,
      sampleItems: Array.isArray(privacy.rows) ? privacy.rows.length : 0,
      reportScopeExcludedItems: scopedEvidence.summary?.excludedItems || 0,
      reportScopeFilters: scopedEvidence.summary?.filtersApplied || 0,
      excludedFields: privacy.excludedFields?.length || 0,
      anonymizedFields: privacy.anonymizedFields?.length || 0,
    },
    privacy,
    reportScope: scopedEvidence.summary || null,
  };

  return { runtimeArgs, debug };
};
